import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"

const inputFileVariants = cva(
  // bg: --general-molecules-bg-bg-white | shadow: --xs-* tokens | no token for flex/w/transition
  "flex w-80 flex-col justify-center items-start border transition-shadow font-[var(--font-family--sans)] " +
  "bg-[var(--general-molecules-bg-bg-white)] " +
  "shadow-[var(--xs-x)_var(--xs-y)_var(--xs-blur)_var(--xs-spread)_var(--xs-color)]",
  {
    variants: {
      variant: {
        // border: --general-border-solid-30 | focus ring: --focus-ring
        default:
          "border-[var(--general-border-solid-30)] " +
          "focus-within:shadow-[0_0_0_3px_var(--focus-ring)]",
        // border: --general-border-destructive | focus ring: --focus-ring-error
        error:
          "border-[var(--general-border-destructive)] " +
          "focus-within:shadow-[0_0_0_3px_var(--focus-ring-error)]",
      },
      inputType: {
        default: "",
        // radius: --semantic-rounded-full
        round: "rounded-[var(--semantic-rounded-full)]",
      },
      size: {
        // min-h: --semantic-3xl (40px) | py: Tailwind py-2.5 (0.625remâ‰ˆ10px) | gap: --absolute-3
        large:   "min-h-[var(--semantic-3xl)] py-2.5 gap-[var(--absolute-3)]",
        // min-h: --absolute-9 (36px) | py: --semantic-xs (8px) | gap: --semantic-xs
        regular: "min-h-[var(--absolute-9)] py-[var(--semantic-xs)] gap-[var(--semantic-xs)]",
        // min-h: Tailwind min-h-8 (2rem=32px) | py: --absolute-15 (6px) | gap: --absolute-15
        small:   "min-h-8 py-[var(--absolute-15)] gap-[var(--absolute-15)]",
        // min-h: Tailwind min-h-6 (1.5rem=24px) | py: Tailwind py-1 (0.25rem=4px) | gap: --semantic-2xs
        mini:    "min-h-6 py-1 gap-[var(--semantic-2xs)]",
      },
    },
    compoundVariants: [
      // radius: --semantic-rounded-lg | px: --semantic-md / --absolute-3 / --semantic-xs / --absolute-15
      { inputType: "default", size: "large",   class: "rounded-[var(--semantic-rounded-lg)] px-[var(--semantic-md)]" },
      { inputType: "default", size: "regular", class: "rounded-[var(--semantic-rounded-lg)] px-[var(--absolute-3)]" },
      { inputType: "default", size: "small",   class: "rounded-[var(--semantic-rounded-lg)] px-[var(--semantic-xs)]" },
      // mini uses rounded-sm (4px) per spec
      { inputType: "default", size: "mini",    class: "rounded-[var(--semantic-rounded-sm)] px-[var(--absolute-15)]" },
      // round: radius already set on inputType variant; only px needed
      { inputType: "round",   size: "large",   class: "px-[var(--semantic-md)]" },
      { inputType: "round",   size: "regular", class: "px-[var(--absolute-3)]" },
      { inputType: "round",   size: "small",   class: "px-[var(--semantic-xs)]" },
      // round/mini uses semantic-xs (8px) not absolute-15 (6px) per spec
      { inputType: "round",   size: "mini",    class: "px-[var(--semantic-xs)]" },
    ],
    defaultVariants: {
      variant: "default",
      inputType: "default",
      size: "regular",
    },
  }
)

export interface InputFileProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type">,
    VariantProps<typeof inputFileVariants> {}

const InputFile = React.forwardRef<HTMLInputElement, InputFileProps>(
  ({ className, variant, size, inputType, disabled, ...props }, ref) => {
    return (
      <div
        className={cn(
          inputFileVariants({ variant, size, inputType }),
          disabled && "opacity-40 cursor-not-allowed border-dashed bg-muted",
          className
        )}
      >
        <input
          ref={ref}
          type="file"
          disabled={disabled}
          className={cn(
            "w-full bg-transparent outline-none",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
            // font-size: --font-size-s (14px) or --font-size-xs (12px) for mini
            size === "mini"
              ? "text-[var(--font-size-xs)] file:text-[var(--font-size-xs)]"
              : "text-[var(--font-size-s)] file:text-[var(--font-size-s)]",
            // color: --general-text-secondary
            "text-[var(--general-text-secondary)]",
            "file:border-0 file:bg-transparent file:font-[var(--font-family--sans)] file:font-medium file:text-[var(--general-text-secondary)] file:mr-2",
            disabled ? "file:cursor-not-allowed" : "file:cursor-pointer",
          )}
          {...props}
        />
      </div>
    )
  }
)
InputFile.displayName = "InputFile"

export { InputFile, inputFileVariants }
